import { useState } from "react";
import axios from "axios";
import { Loader2, Sparkles, RotateCcw } from "lucide-react";
import { Reveal } from "./Reveal";
import { Star } from "./Logo";
import { scrollToSection } from "./Navbar";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Chip = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300"
    style={
      active
        ? { background: "var(--camel)", borderColor: "var(--camel)", color: "#fff" }
        : { background: "var(--surface)", borderColor: "var(--line)", color: "var(--on-surface)" }
    }
  >
    {label}
  </button>
);

export default function StyleCheck() {
  const { t, lang } = useLang();
  const s = t.styleCheck;
  const [answers, setAnswers] = useState({ occasion: "", hair: "", eyes: "", style: "" });
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState("");

  const questions = [
    { key: "occasion", label: s.qOccasion, options: s.occasions },
    { key: "hair", label: s.qHair, options: s.hairs },
    { key: "eyes", label: s.qEyes, options: s.eyes },
    { key: "style", label: s.qStyle, options: s.styles },
  ];
  const complete = Object.values(answers).every(Boolean);

  const submit = async () => {
    if (!complete || status === "loading") return;
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/style-check`, { ...answers, lang });
      setResult(res.data.result);
      setStatus("done");
    } catch (err) {
      setResult(err?.response?.status === 429 ? s.rateLimited : s.error);
      setStatus("error");
    }
  };

  const reset = () => {
    setAnswers({ occasion: "", hair: "", eyes: "", style: "" });
    setResult("");
    setStatus("idle");
  };

  return (
    <section id="style-check" className="py-24 sm:py-36" style={{ background: "var(--surface-secondary)" }} data-testid="stylecheck-section">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="flex items-center justify-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
            <Star size={11} /> {s.kicker}
          </p>
          <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl font-semibold leading-tight" data-testid="stylecheck-title">
            {s.title}
          </h2>
          <p className="mt-4 text-base md:text-lg" style={{ color: "var(--muted-warm)" }}>{s.sub}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 rounded-3xl border p-8 sm:p-10" style={{ background: "var(--surface)", borderColor: "var(--line)" }} data-testid="stylecheck-card">
            {status !== "done" && (
              <>
                <div className="space-y-8">
                  {questions.map((q) => (
                    <div key={q.key} data-testid={`stylecheck-q-${q.key}`}>
                      <p className="mb-3 text-sm font-bold uppercase" style={{ letterSpacing: "0.1em", color: "var(--muted-warm)" }}>{q.label}</p>
                      <div className="flex flex-wrap gap-2.5">
                        {q.options.map((opt) => (
                          <Chip
                            key={opt}
                            label={opt}
                            testId={`stylecheck-opt-${q.key}-${q.options.indexOf(opt)}`}
                            active={answers[q.key] === opt}
                            onClick={() => setAnswers((a) => ({ ...a, [q.key]: opt }))}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={submit}
                  disabled={!complete || status === "loading"}
                  data-testid="stylecheck-submit-btn"
                  className="pill-btn mt-10 flex w-full items-center justify-center gap-2 rounded-full py-4 text-sm font-medium text-white disabled:opacity-50"
                  style={{ background: "var(--camel)" }}
                >
                  {status === "loading" ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  {status === "loading" ? s.loading : s.submit}
                </button>
                {status === "error" && (
                  <p data-testid="stylecheck-error" className="mt-5 rounded-2xl px-5 py-3.5 text-sm text-center" style={{ background: "var(--rose-soft)" }}>
                    {result}
                  </p>
                )}
              </>
            )}

            {status === "done" && (
              <div data-testid="stylecheck-result">
                <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
                  <Star size={11} /> {s.resultTitle}
                </p>
                <p className="mt-5 whitespace-pre-line font-serif-display text-xl sm:text-2xl leading-relaxed">{result}</p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={() => scrollToSection("download")}
                    data-testid="stylecheck-cta-app"
                    className="pill-btn flex-1 rounded-full py-3.5 text-sm font-medium text-white"
                    style={{ background: "var(--camel)" }}
                  >
                    {s.ctaApp}
                  </button>
                  <button
                    onClick={() => scrollToSection("boutiquen")}
                    data-testid="stylecheck-cta-b2b"
                    className="pill-btn flex-1 rounded-full border py-3.5 text-sm font-medium"
                    style={{ borderColor: "var(--camel)", color: "var(--camel)", background: "var(--surface)" }}
                  >
                    {s.ctaB2b}
                  </button>
                </div>
                <button
                  onClick={reset}
                  data-testid="stylecheck-reset-btn"
                  className="mx-auto mt-6 flex items-center gap-1.5 text-xs font-medium transition-colors duration-300 hover:text-[var(--camel)]"
                  style={{ color: "var(--muted-warm)" }}
                >
                  <RotateCcw size={12} /> {s.again}
                </button>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
